import { getTileHeightAllowedValues, updateSliderDisplay } from './config/sliders.js';
import { state } from './state.js';
import * as UI from './ui.js';
import { getOriginalSize } from './target_controller.js';

const MODE_KEY = 'mosaic_settings_mode';
const ADMIN_CONTROLS_KEY = 'mosaic_admin_controls_enabled';

const SIMPLE_DEFAULTS = {
    detail: 55,
    color: 50,
    variety: 50,
};

const DETAIL_RANGE = { min: 0, max: 100 };
const COLOR_RANGE = { min: 0, max: 100 };
const VARIETY_RANGE = { min: 0, max: 100 };

const TILE_HEIGHT_VALUES = getTileHeightAllowedValues();
const SIMPLE_TILE_HEIGHT_BOUNDS = Object.freeze({ min: 20, max: 100 });
const SIMPLE_TILE_HEIGHT_VALUES = Object.freeze(
    TILE_HEIGHT_VALUES.filter((value) => (
        Number.isFinite(Number(value))
        && Number(value) >= SIMPLE_TILE_HEIGHT_BOUNDS.min
        && Number(value) <= SIMPLE_TILE_HEIGHT_BOUNDS.max
    ))
);
const TILE_HEIGHT_RANGE = {
    min: TILE_HEIGHT_VALUES[0],
    max: TILE_HEIGHT_VALUES[TILE_HEIGHT_VALUES.length - 1],
    step: 1,
};
const COLOR_SHARE_RANGE = { min: 0, max: 100, step: 1 };
const REUSE_RANGE = { min: 0, max: 100, step: 1 };
const NOISE_RANGE = { min: 0, max: 100, step: 1 };
const ADAPTIVE_SQUARE_VALUES = Object.freeze([10, 20, 30, 40]);

let _isSyncing = false;

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function mapRange(value, inMin, inMax, outMin, outMax) {
    if (inMax === inMin) return outMin;
    const pct = (value - inMin) / (inMax - inMin);
    return outMin + pct * (outMax - outMin);
}

function roundStep(value, step) {
    if (!step) return value;
    const inv = 1 / step;
    return Math.round(value * inv) / inv;
}

function readNumber(el, fallback = 0) {
    if (!el) return fallback;
    const num = Number(el.value);
    return Number.isFinite(num) ? num : fallback;
}

function pickClosest(value, allowedValues, fallback) {
    const values = Array.isArray(allowedValues) ? allowedValues : [];
    if (!values.length) return fallback;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return values.includes(fallback) ? fallback : values[0];
    let best = Number(values[0]);
    let bestDist = Math.abs(numeric - best);
    for (let i = 1; i < values.length; i += 1) {
        const candidate = Number(values[i]);
        const dist = Math.abs(numeric - candidate);
        if (dist < bestDist) {
            best = candidate;
            bestDist = dist;
        }
    }
    return best;
}

function getSimpleTileHeightValues() {
    return SIMPLE_TILE_HEIGHT_VALUES.length ? Array.from(SIMPLE_TILE_HEIGHT_VALUES) : Array.from(TILE_HEIGHT_VALUES);
}

function setInputValue(el, value) {
    if (!el) return;
    el.value = String(value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
}

function describeDetail(value) {
    if (value < 25) return 'Ultra';
    if (value < 50) return 'Detailed';
    if (value < 75) return 'Soft';
    return 'Blocky';
}

function describeColor(value) {
    if (value < 25) return 'Subtle';
    if (value < 50) return 'Balanced';
    if (value < 75) return 'Strong';
    return 'Vivid';
}

function describeVariety(value) {
    if (value < 25) return 'Clean';
    if (value < 50) return 'Mixed';
    if (value < 75) return 'Varied';
    return 'Wild';
}

function detailToTileHeight(detail) {
    const simpleTileHeights = getSimpleTileHeightValues();
    if (!simpleTileHeights.length) return TILE_HEIGHT_RANGE.max;
    const rawIndex = mapRange(detail, DETAIL_RANGE.min, DETAIL_RANGE.max, 0, simpleTileHeights.length - 1);
    const index = clamp(Math.round(rawIndex), 0, simpleTileHeights.length - 1);
    return simpleTileHeights[index];
}

function colorToShare(color) {
    const raw = mapRange(color, COLOR_RANGE.min, COLOR_RANGE.max, COLOR_SHARE_RANGE.min, COLOR_SHARE_RANGE.max);
    return clamp(roundStep(raw, COLOR_SHARE_RANGE.step), COLOR_SHARE_RANGE.min, COLOR_SHARE_RANGE.max);
}

function varietyToReuse(variety) {
    const raw = mapRange(variety, VARIETY_RANGE.min, VARIETY_RANGE.max, REUSE_RANGE.min, REUSE_RANGE.max);
    return clamp(roundStep(raw, REUSE_RANGE.step), REUSE_RANGE.min, REUSE_RANGE.max);
}

function varietyToNoise(variety) {
    const raw = mapRange(variety, VARIETY_RANGE.min, VARIETY_RANGE.max, NOISE_RANGE.min, NOISE_RANGE.max);
    return clamp(roundStep(raw, NOISE_RANGE.step), NOISE_RANGE.min, NOISE_RANGE.max);
}

function tileHeightToDetail(tileHeight) {
    const simpleTileHeights = getSimpleTileHeightValues();
    if (!simpleTileHeights.length) return DETAIL_RANGE.min;
    const fallback = simpleTileHeights[0];
    const snapped = pickClosest(tileHeight, simpleTileHeights, fallback);
    const index = simpleTileHeights.indexOf(snapped);
    if (index < 0) return DETAIL_RANGE.min;
    return clamp(
        mapRange(index, 0, simpleTileHeights.length - 1, DETAIL_RANGE.min, DETAIL_RANGE.max),
        DETAIL_RANGE.min,
        DETAIL_RANGE.max,
    );
}


function shareToColor(share) {
    return clamp(mapRange(share, COLOR_SHARE_RANGE.min, COLOR_SHARE_RANGE.max, COLOR_RANGE.min, COLOR_RANGE.max), COLOR_RANGE.min, COLOR_RANGE.max);
}

function reuseToVariety(reuse) {
    return clamp(mapRange(reuse, REUSE_RANGE.min, REUSE_RANGE.max, VARIETY_RANGE.min, VARIETY_RANGE.max), VARIETY_RANGE.min, VARIETY_RANGE.max);
}

function noiseToVariety(noise) {
    return clamp(mapRange(noise, NOISE_RANGE.min, NOISE_RANGE.max, VARIETY_RANGE.min, VARIETY_RANGE.max), VARIETY_RANGE.min, VARIETY_RANGE.max);
}

function updateSimpleLabels(detailVal, colorVal, varietyVal) {
    const detailLabel = document.getElementById('simpleDetailVal');
    const colorLabel = document.getElementById('simpleColorVal');
    const varietyLabel = document.getElementById('simpleVarietyVal');

    if (detailLabel) detailLabel.textContent = describeDetail(detailVal);
    if (colorLabel) colorLabel.textContent = describeColor(colorVal);
    if (varietyLabel) varietyLabel.textContent = describeVariety(varietyVal);
    updateSimpleDetailExtremeHint(detailVal);
}

function updateSimpleDetailExtremeHint(detailVal) {
    const hint = document.getElementById('simpleDetailExtremeHint');
    if (!hint) return;
    const value = Number(detailVal);
    const isExtreme = Number.isFinite(value)
        && (Math.round(value) <= DETAIL_RANGE.min || Math.round(value) >= DETAIL_RANGE.max);
    hint.classList.toggle('hidden', !isExtreme);
}

function applySimpleToAdvanced() {
    if (_isSyncing) return;
    _isSyncing = true;

    const detail = readNumber(document.getElementById('simpleDetail'), SIMPLE_DEFAULTS.detail);
    const color = readNumber(document.getElementById('simpleColor'), SIMPLE_DEFAULTS.color);
    const variety = readNumber(document.getElementById('simpleVariety'), SIMPLE_DEFAULTS.variety);

    const tileHeightInput = document.getElementById('thInput');
    const colorShareInput = document.getElementById('colorShare');
    const reuseInput = document.getElementById('rpInput');
    const noiseInput = document.getElementById('noiseFactor');

    setInputValue(tileHeightInput, detailToTileHeight(detail));
    setInputValue(colorShareInput, colorToShare(color));
    setInputValue(reuseInput, varietyToReuse(variety));
    setInputValue(noiseInput, varietyToNoise(variety));

    updateSliderDisplay('thInput', tileHeightInput ? tileHeightInput.value : detailToTileHeight(detail));
    updateSliderDisplay('colorShare', colorShareInput ? colorShareInput.value : colorToShare(color));
    updateSliderDisplay('noiseFactor', noiseInput ? noiseInput.value : varietyToNoise(variety));
    updateSliderDisplay('rpInput', reuseInput ? reuseInput.value : varietyToReuse(variety));

    updateSimpleLabels(detail, color, variety);
    _isSyncing = false;
}

function syncSimpleFromAdvanced() {
    if (_isSyncing) return;
    _isSyncing = true;

    const tileHeightInput = document.getElementById('thInput');
    const colorShareInput = document.getElementById('colorShare');
    const reuseInput = document.getElementById('rpInput');
    const noiseInput = document.getElementById('noiseFactor');

    const tileHeightVal = readNumber(tileHeightInput, TILE_HEIGHT_RANGE.max);
    const colorShareVal = readNumber(colorShareInput, COLOR_SHARE_RANGE.min);
    const reuseVal = readNumber(reuseInput, REUSE_RANGE.min);
    const noiseVal = readNumber(noiseInput, NOISE_RANGE.min);

    const detail = clamp(Math.round(tileHeightToDetail(tileHeightVal)), DETAIL_RANGE.min, DETAIL_RANGE.max);

    const color = clamp(Math.round(shareToColor(colorShareVal)), COLOR_RANGE.min, COLOR_RANGE.max);

    const varietyFromReuse = reuseToVariety(reuseVal);
    const varietyFromNoise = noiseToVariety(noiseVal);
    const variety = clamp(Math.round((varietyFromReuse + varietyFromNoise) / 2), VARIETY_RANGE.min, VARIETY_RANGE.max);

    const simpleDetail = document.getElementById('simpleDetail');
    const simpleColor = document.getElementById('simpleColor');
    const simpleVariety = document.getElementById('simpleVariety');
    if (simpleDetail) simpleDetail.value = String(detail);
    if (simpleColor) simpleColor.value = String(color);
    if (simpleVariety) simpleVariety.value = String(variety);

    updateSimpleLabels(detail, color, variety);
    _isSyncing = false;
}

function normalizeMode(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === 'nerd' || raw === 'pro') return 'nerd';
    return 'simple';
}

function getActiveMode() {
    const toggle = document.getElementById('settingsMode');
    const active = toggle ? toggle.querySelector('.seg-btn.active') : null;
    return normalizeMode(active ? active.getAttribute('data-mode') : getStoredMode());
}

export function getCurrentSettingsMode() {
    return getActiveMode();
}

export function getSimpleDetailExtremeEdge() {
    if (getActiveMode() !== 'simple') return '';
    const detail = readNumber(document.getElementById('simpleDetail'), NaN);
    if (!Number.isFinite(detail)) return '';
    if (Math.round(detail) <= DETAIL_RANGE.min) return 'min';
    if (Math.round(detail) >= DETAIL_RANGE.max) return 'max';
    return '';
}

function isLegacyHighresAvailable() {
    const plan = state.plan || {};
    return Boolean(state.isAdmin || plan.legacy_highres_draft_available);
}

function isAdminControlsAvailable() {
    return Boolean(state.isAdmin);
}

function getStoredAdminControlsEnabled() {
    try {
        return String(localStorage.getItem(ADMIN_CONTROLS_KEY) || '').trim() === '1';
    } catch (e) {
        return false;
    }
}

function setStoredAdminControlsEnabled(enabled) {
    try {
        if (enabled) localStorage.setItem(ADMIN_CONTROLS_KEY, '1');
        else localStorage.removeItem(ADMIN_CONTROLS_KEY);
    } catch (e) {}
}

export function isAdminControlsEnabled() {
    const toggle = document.getElementById('adminControlsToggle');
    return Boolean(toggle && toggle.checked && isAdminControlsAvailable());
}

export function isLegacyHighresDraftEnabled() {
    const toggle = document.getElementById('legacyHighresDraftToggle');
    return Boolean(toggle && toggle.checked && isLegacyHighresAvailable() && isAdminControlsEnabled());
}

export function getSelectedMosaicLayout() {
    if (!isAdminControlsEnabled()) return 'IRREGULAR';
    const select = document.getElementById('modeSelect');
    const value = String((select && select.value) || 'IRREGULAR').trim().toUpperCase();
    return value === 'QUADTREE' ? 'QUADTREE' : 'IRREGULAR';
}

export function isAdaptiveSquaresSelected() {
    return getSelectedMosaicLayout() === 'QUADTREE';
}

function emitLegacyModeChange() {
    document.dispatchEvent(new CustomEvent('draft:legacy_mode_changed', {
        detail: {
            enabled: isLegacyHighresDraftEnabled(),
            available: isLegacyHighresAvailable(),
        },
    }));
}

function emitAdaptiveLayoutChange() {
    document.dispatchEvent(new CustomEvent('draft:adaptive_layout_changed', {
        detail: {
            admin_enabled: isAdminControlsEnabled(),
            layout: getSelectedMosaicLayout(),
            adaptive_enabled: isAdaptiveSquaresSelected(),
        },
    }));
}

function refreshOutputEstimateVisibility() {
    const outputEstimate = document.getElementById('outputEstimateGroup');
    if (!outputEstimate) return;
    const mode = getActiveMode();
    const show = mode === 'nerd' && isLegacyHighresDraftEnabled();
    outputEstimate.classList.toggle('hidden', !show);
}

export function refreshLegacyDraftControls() {
    const adminControlsContainer = document.getElementById('adminControlsContainer');
    const adminControlsToggle = document.getElementById('adminControlsToggle');
    const qualityRow = document.getElementById('qualityBuildRow');
    const qualityToggle = document.getElementById('qualityToggle');
    const mosaicLayoutRow = document.getElementById('mosaicLayoutRow');
    const modeSelect = document.getElementById('modeSelect');
    const adaptiveSquareMinSizeRow = document.getElementById('adaptiveSquareMinSizeRow');
    const adaptiveSquareMinSizeInput = document.getElementById('adaptiveSquareMinSizeInput');
    const matcherBackendRow = document.getElementById('matcherBackendRow');
    const tileHeightRow = document.getElementById('tileHeightRow');
    const container = document.getElementById('draftSizingAdminContainer');
    const toggleRow = document.getElementById('legacyDraftToggleRow');
    const toggle = document.getElementById('legacyHighresDraftToggle');
    const scaleGroup = document.getElementById('legacyScaleGroup');
    const available = isLegacyHighresAvailable();
    const adminAvailable = isAdminControlsAvailable();

    if (adminControlsContainer) adminControlsContainer.classList.toggle('hidden', !adminAvailable);
    if (adminControlsToggle && !adminAvailable) adminControlsToggle.checked = false;

    const adminEnabled = isAdminControlsEnabled();
    if (adminControlsToggle) setStoredAdminControlsEnabled(adminEnabled);

    if (qualityRow) qualityRow.classList.toggle('hidden', !adminEnabled);
    if (mosaicLayoutRow) mosaicLayoutRow.classList.toggle('hidden', !adminEnabled);
    if (matcherBackendRow) matcherBackendRow.classList.toggle('hidden', !adminEnabled);
    const adaptiveSelected = adminEnabled && modeSelect && String(modeSelect.value || '').trim().toUpperCase() === 'QUADTREE';
    if (adaptiveSquareMinSizeRow) adaptiveSquareMinSizeRow.classList.toggle('hidden', !adaptiveSelected);
    if (tileHeightRow) tileHeightRow.classList.toggle('hidden', adaptiveSelected);

    if (adaptiveSquareMinSizeInput) {
        const nextValue = Number(adaptiveSquareMinSizeInput.value);
        if (!ADAPTIVE_SQUARE_VALUES.includes(nextValue)) {
            adaptiveSquareMinSizeInput.value = String(ADAPTIVE_SQUARE_VALUES[0]);
        }
    }

    if (qualityToggle) {
        qualityToggle.disabled = Boolean(adaptiveSelected);
    }
    if (qualityToggle && (!adminEnabled || adaptiveSelected)) {
        if (!qualityToggle.checked) {
            qualityToggle.checked = true;
            qualityToggle.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
            qualityToggle.checked = true;
        }
    }

    const showLegacyControls = Boolean(available && adminEnabled);
    if (container) container.classList.toggle('hidden', !showLegacyControls);
    if (toggleRow) toggleRow.classList.toggle('hidden', !showLegacyControls);
    if (toggle && !showLegacyControls) toggle.checked = false;
    if (scaleGroup) scaleGroup.classList.toggle('hidden', !showLegacyControls);

    const { origW, origH } = getOriginalSize();
    if (origW && origH) {
        UI.updateStats(origW, origH);
    }
    refreshOutputEstimateVisibility();
    emitLegacyModeChange();
    emitAdaptiveLayoutChange();
}

function bindLegacyDraftToggle() {
    const toggle = document.getElementById('legacyHighresDraftToggle');
    if (!toggle || toggle.dataset.bound === '1') return;
    toggle.dataset.bound = '1';
    toggle.addEventListener('change', () => {
        refreshLegacyDraftControls();
    });
}

function bindAdminControlsToggle() {
    const toggle = document.getElementById('adminControlsToggle');
    if (!toggle || toggle.dataset.bound === '1') return;
    toggle.dataset.bound = '1';
    toggle.addEventListener('change', () => {
        refreshLegacyDraftControls();
    });
}

function bindAdaptiveLayoutControls() {
    const modeSelect = document.getElementById('modeSelect');
    if (modeSelect && modeSelect.dataset.bound !== '1') {
        modeSelect.dataset.bound = '1';
        modeSelect.addEventListener('change', () => {
            refreshLegacyDraftControls();
        });
    }

    const adaptiveSquareMinSizeInput = document.getElementById('adaptiveSquareMinSizeInput');
    if (adaptiveSquareMinSizeInput && adaptiveSquareMinSizeInput.dataset.bound !== '1') {
        adaptiveSquareMinSizeInput.dataset.bound = '1';
        adaptiveSquareMinSizeInput.addEventListener('change', () => {
            emitAdaptiveLayoutChange();
        });
    }
}

function setMode(mode) {
    const normalized = normalizeMode(mode);
    const simpleControls = document.getElementById('simpleControls');
    const proControls = document.getElementById('advancedControls') || document.getElementById('proControls');
    const presetRow = document.getElementById('presetRow');
    const toggle = document.getElementById('settingsMode');
    const buttons = toggle ? Array.from(toggle.querySelectorAll('.seg-btn')) : [];

    const isNerd = normalized === 'nerd';
    if (simpleControls) simpleControls.classList.toggle('hidden', isNerd);
    if (proControls) proControls.classList.toggle('hidden', !isNerd);
    if (presetRow) presetRow.classList.toggle('hidden', isNerd);

    buttons.forEach((btn) => {
        const btnMode = btn.getAttribute('data-mode');
        btn.classList.toggle('active', btnMode === normalized);
    });

    refreshOutputEstimateVisibility();

    if (!isNerd) {
        syncSimpleFromAdvanced();
        applySimpleToAdvanced();
    } else {
        updateSimpleDetailExtremeHint(NaN);
    }
}

function getStoredMode() {
    return 'simple';
}

function bindModeToggle() {
    const toggle = document.getElementById('settingsMode');
    if (!toggle) return;
    toggle.addEventListener('click', (event) => {
        const target = event.target;
        if (!target || !target.matches('.seg-btn')) return;
        const mode = target.getAttribute('data-mode');
        if (!mode) return;
        setMode(mode);
    });
}

function bindSimpleControls() {
    const detail = document.getElementById('simpleDetail');
    const color = document.getElementById('simpleColor');
    const variety = document.getElementById('simpleVariety');
    if (detail) detail.addEventListener('input', applySimpleToAdvanced);
    if (color) color.addEventListener('input', applySimpleToAdvanced);
    if (variety) variety.addEventListener('input', applySimpleToAdvanced);
}

function bindAdvancedControls() {
    const tileHeightInput = document.getElementById('thInput');
    const colorShareInput = document.getElementById('colorShare');
    const reuseInput = document.getElementById('rpInput');
    const noiseInput = document.getElementById('noiseFactor');
    const sync = () => syncSimpleFromAdvanced();

    if (tileHeightInput) tileHeightInput.addEventListener('input', sync);
    if (colorShareInput) colorShareInput.addEventListener('input', sync);
    if (reuseInput) reuseInput.addEventListener('input', sync);
    if (noiseInput) noiseInput.addEventListener('input', sync);
    const qualityToggle = document.getElementById('qualityToggle');
    if (qualityToggle) qualityToggle.addEventListener('change', sync);
}

export function initSettingsMode() {
    const mode = getStoredMode();
    try {
        localStorage.removeItem(MODE_KEY);
    } catch (e) {}

    bindModeToggle();
    bindSimpleControls();
    bindAdvancedControls();
    bindLegacyDraftToggle();
    bindAdminControlsToggle();
    bindAdaptiveLayoutControls();

    const adminToggle = document.getElementById('adminControlsToggle');
    if (adminToggle) {
        adminToggle.checked = isAdminControlsAvailable() && getStoredAdminControlsEnabled();
    }

    const simpleDetail = document.getElementById('simpleDetail');
    const simpleColor = document.getElementById('simpleColor');
    const simpleVariety = document.getElementById('simpleVariety');
    if (simpleDetail) simpleDetail.value = String(SIMPLE_DEFAULTS.detail);
    if (simpleColor) simpleColor.value = String(SIMPLE_DEFAULTS.color);
    if (simpleVariety) simpleVariety.value = String(SIMPLE_DEFAULTS.variety);

    updateSimpleLabels(SIMPLE_DEFAULTS.detail, SIMPLE_DEFAULTS.color, SIMPLE_DEFAULTS.variety);
    setMode(mode);
    refreshLegacyDraftControls();
    refreshOutputEstimateVisibility();
    syncSimpleFromAdvanced();
}
